import React, { useState } from 'react';

interface Question {
  question: string;
  options: string[];
  correct_index: number;
  explanation: string;
}

interface MultipleChoiceExerciseProps {
  questions: Question[];
  onSubmit: (answers: number[]) => void;
}

/**
 * MultipleChoiceExercise Component
 *
 * Displays multiple choice questions with radio button options.
 */
const MultipleChoiceExercise: React.FC<MultipleChoiceExerciseProps> = ({ questions, onSubmit }) => {
  const [selectedAnswers, setSelectedAnswers] = useState<{ [key: number]: number }>({});

  const handleSelectOption = (questionIndex: number, optionIndex: number) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [questionIndex]: optionIndex
    });
  };

  const handleSubmit = () => {
    const answers = questions.map((_, index) => selectedAnswers[index] ?? -1);
    onSubmit(answers);
  };

  const isComplete = Object.keys(selectedAnswers).length === questions.length;

  return (
    <div className="space-y-8">
      {questions.map((question, qIndex) => (
        <div key={qIndex} className="card bg-base-200">
          <div className="card-body">
            <h3 className="card-title text-lg mb-4">
              <span className="badge badge-primary mr-2">{qIndex + 1}</span>
              {question.question}
            </h3>

            <div className="space-y-3">
              {question.options.map((option, oIndex) => {
                const isSelected = selectedAnswers[qIndex] === oIndex;

                return (
                  <label
                    key={oIndex}
                    className={`
                      flex items-center p-4 rounded-lg cursor-pointer transition-all
                      border-2
                      ${isSelected
                        ? 'border-primary bg-primary/10'
                        : 'border-base-300 hover:border-primary/50 bg-base-100'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name={`question-${qIndex}`}
                      className="radio radio-primary"
                      checked={isSelected}
                      onChange={() => handleSelectOption(qIndex, oIndex)}
                    />
                    <span className="ml-3 flex-1">{option}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      <div className="flex justify-center pt-4">
        <button
          onClick={handleSubmit}
          className="btn btn-primary btn-lg"
          disabled={!isComplete}
        >
          {isComplete
            ? 'Submit Answers'
            : `Answer all questions (${Object.keys(selectedAnswers).length}/${questions.length})`
          }
        </button>
      </div>
    </div>
  );
};

export default MultipleChoiceExercise;
